const SHAREPOINT_NO_EXPIRY_DAYS_VALUE = -1
const SHAREPOINT_MARK_NEW_FILES_SENSITIVE_BLOCK_EXTERNAL_SHARING = 'BlockExternalSharing'
const SHAREPOINT_MARK_NEW_FILES_SENSITIVE_ALLOW_EXTERNAL_SHARING = 'AllowExternalSharing'
const ANONYMOUS_LINK_EXPIRY_NOT_APPLICABLE_MESSAGE_ID =
  'AI_RISK_REPORT.SHAREPOINT.REQUIRE_ANONYMOUS_LINKS_EXPIRE_IN_DAYS.NOT_APPLICABLE_REASON'

const SHAREPOINT_DEFAULT_SHARING_LINK_TYPE_MESSAGE_ID_BY_VALUE = {
  0: 'AI_RISK_REPORT.SHAREPOINT.DEFAULT_SHARING_LINK_TYPE.NONE',
  1: 'AI_RISK_REPORT.SHAREPOINT.DEFAULT_SHARING_LINK_TYPE.SPECIFIC_PEOPLE',
  2: 'AI_RISK_REPORT.SHAREPOINT.DEFAULT_SHARING_LINK_TYPE.ORGANIZATION_ONLY',
  3: 'AI_RISK_REPORT.SHAREPOINT.DEFAULT_SHARING_LINK_TYPE.ANYONE',
  4: 'AI_RISK_REPORT.SHAREPOINT.DEFAULT_SHARING_LINK_TYPE.EXISTING_ACCESS',
}

const SHAREPOINT_DEFAULT_LINK_PERMISSION_MESSAGE_ID_BY_VALUE = {
  0: 'AI_RISK_REPORT.SHAREPOINT.DEFAULT_LINK_PERMISSION.NONE',
  1: 'AI_RISK_REPORT.SHAREPOINT.DEFAULT_LINK_PERMISSION.VIEW',
  2: 'AI_RISK_REPORT.SHAREPOINT.DEFAULT_LINK_PERMISSION.EDIT',
}

const hasMeasuredValue = (value) => value !== undefined && value !== null && value !== ''

const formatMessage = (intl, id, values) => intl.formatMessage({ id }, values)

const formatEnumValue = (intl, messageIdByValue, value) => {
  if (!hasMeasuredValue(value)) return null

  const messageId = messageIdByValue[String(value)]
  if (messageId) return `${value} (${formatMessage(intl, messageId)})`
  return String(value)
}

const normalizeBoolean = (value) => {
  if (value === true || value === false) return value

  const normalizedValue = String(value ?? '')
    .trim()
    .toLowerCase()
  if (normalizedValue === 'true' || normalizedValue === '1') return true
  if (normalizedValue === 'false' || normalizedValue === '0') return false
  return null
}

const formatBooleanValue = (value) => {
  const normalizedValue = normalizeBoolean(value)
  if (normalizedValue === true || normalizedValue === false) return String(normalizedValue)
  if (hasMeasuredValue(value)) return String(value)
  return null
}

const formatRecommendedBooleanValue = (value) => String(value)

const formatDaysValue = (intl, value) => {
  if (!hasMeasuredValue(value)) return null

  const dayCount = Number(value)
  if (!Number.isFinite(dayCount)) return String(value)

  return formatMessage(intl, 'AI_RISK_REPORT.SHAREPOINT.EXTERNAL_USER_EXPIRE_IN_DAYS.VALUE', {
    count: dayCount,
  })
}

const formatAnonymousLinksExpireInDaysValue = (intl, value) => {
  if (!hasMeasuredValue(value)) return null

  const dayCount = Number(value)
  if (dayCount === SHAREPOINT_NO_EXPIRY_DAYS_VALUE) {
    return formatMessage(intl, 'AI_RISK_REPORT.SHAREPOINT.REQUIRE_ANONYMOUS_LINKS_EXPIRE_IN_DAYS.NO_EXPIRY')
  }

  return formatDaysValue(intl, value)
}

const normalizeMarkNewFilesSensitiveByDefault = (value) => {
  if (value === SHAREPOINT_MARK_NEW_FILES_SENSITIVE_BLOCK_EXTERNAL_SHARING) return true
  if (value === SHAREPOINT_MARK_NEW_FILES_SENSITIVE_ALLOW_EXTERNAL_SHARING) return false
  return normalizeBoolean(value)
}

const formatMarkNewFilesSensitiveByDefaultValue = (value) => {
  const normalizedValue = normalizeMarkNewFilesSensitiveByDefault(value)
  if (normalizedValue === true || normalizedValue === false) return String(normalizedValue)
  if (hasMeasuredValue(value)) return String(value)
  return null
}

export const buildSharePointTenantSettingRows = (assessments, intl) => {
  const assessmentByKey = Object.fromEntries(assessments.map((assessment) => [assessment.key, assessment]))
  const getAssessment = (key) => assessmentByKey[key] ?? { current: null, recommended: null, result: null }

  return [
    {
      key: 'sharingCapability',
      sourceKey: 'sharingCapability',
      labelId: 'AI_RISK_REPORT.SETTINGS.SHAREPOINT.SHARING_CAPABILITY',
      ...getAssessment('sharingCapability'),
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.SHAREPOINT.SHARING_CAPABILITY',
    },
    {
      key: 'defaultSharingLinkType',
      sourceKey: 'defaultSharingLinkType',
      labelId: 'AI_RISK_REPORT.SETTINGS.SHAREPOINT.DEFAULT_SHARING_LINK_TYPE',
      ...getAssessment('defaultSharingLinkType'),
      current: formatEnumValue(
        intl,
        SHAREPOINT_DEFAULT_SHARING_LINK_TYPE_MESSAGE_ID_BY_VALUE,
        getAssessment('defaultSharingLinkType').current,
      ),
      recommended: formatEnumValue(
        intl,
        SHAREPOINT_DEFAULT_SHARING_LINK_TYPE_MESSAGE_ID_BY_VALUE,
        getAssessment('defaultSharingLinkType').recommended,
      ),
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.SHAREPOINT.DEFAULT_SHARING_LINK_TYPE',
    },
    {
      key: 'defaultLinkPermission',
      sourceKey: 'defaultLinkPermission',
      labelId: 'AI_RISK_REPORT.SETTINGS.SHAREPOINT.DEFAULT_LINK_PERMISSION',
      ...getAssessment('defaultLinkPermission'),
      current: formatEnumValue(
        intl,
        SHAREPOINT_DEFAULT_LINK_PERMISSION_MESSAGE_ID_BY_VALUE,
        getAssessment('defaultLinkPermission').current,
      ),
      recommended: formatEnumValue(
        intl,
        SHAREPOINT_DEFAULT_LINK_PERMISSION_MESSAGE_ID_BY_VALUE,
        getAssessment('defaultLinkPermission').recommended,
      ),
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.SHAREPOINT.DEFAULT_LINK_PERMISSION',
    },
    {
      key: 'isResharingByExternalUsersEnabled',
      sourceKey: 'isResharingByExternalUsersEnabled',
      labelId: 'AI_RISK_REPORT.SETTINGS.SHAREPOINT.EXTERNAL_USERS_CAN_RESHARE',
      ...getAssessment('isResharingByExternalUsersEnabled'),
      current: formatBooleanValue(getAssessment('isResharingByExternalUsersEnabled').current),
      recommended: formatRecommendedBooleanValue(getAssessment('isResharingByExternalUsersEnabled').recommended),
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.SHAREPOINT.RESHARING_BY_EXTERNAL_USERS_ENABLED',
    },
    {
      key: 'externalUserExpirationRequired',
      sourceKey: 'externalUserExpirationRequired',
      labelId: 'AI_RISK_REPORT.SETTINGS.SHAREPOINT.GUEST_EXPIRATION_REQUIRED',
      ...getAssessment('externalUserExpirationRequired'),
      current: formatBooleanValue(getAssessment('externalUserExpirationRequired').current),
      recommended: formatRecommendedBooleanValue(getAssessment('externalUserExpirationRequired').recommended),
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.SHAREPOINT.EXTERNAL_USER_EXPIRATION_REQUIRED',
    },
    {
      key: 'externalUserExpireInDays',
      sourceKey: 'externalUserExpireInDays',
      labelId: 'AI_RISK_REPORT.SETTINGS.SHAREPOINT.GUEST_EXPIRATION_PERIOD',
      ...getAssessment('externalUserExpireInDays'),
      current: formatDaysValue(intl, getAssessment('externalUserExpireInDays').current),
      recommended: formatMessage(intl, 'AI_RISK_REPORT.SHAREPOINT.EXTERNAL_USER_EXPIRE_IN_DAYS.RECOMMENDED', {
        count: getAssessment('externalUserExpireInDays').recommended,
      }),
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.SHAREPOINT.EXTERNAL_USER_EXPIRE_IN_DAYS',
    },
    {
      key: 'isLegacyAuthProtocolsEnabled',
      sourceKey: 'isLegacyAuthProtocolsEnabled',
      labelId: 'AI_RISK_REPORT.SETTINGS.SHAREPOINT.LEGACY_AUTH_ENABLED',
      ...getAssessment('isLegacyAuthProtocolsEnabled'),
      current: formatBooleanValue(getAssessment('isLegacyAuthProtocolsEnabled').current),
      recommended: formatRecommendedBooleanValue(getAssessment('isLegacyAuthProtocolsEnabled').recommended),
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.SHAREPOINT.LEGACY_AUTH_PROTOCOLS_ENABLED',
    },
    {
      key: 'requireAnonymousLinksExpireInDays',
      sourceKey: 'requireAnonymousLinksExpireInDays',
      labelId: 'AI_RISK_REPORT.SETTINGS.SHAREPOINT.ANONYMOUS_LINK_EXPIRATION',
      ...getAssessment('requireAnonymousLinksExpireInDays'),
      current: formatAnonymousLinksExpireInDaysValue(intl, getAssessment('requireAnonymousLinksExpireInDays').current),
      recommended: formatMessage(intl, 'AI_RISK_REPORT.SHAREPOINT.REQUIRE_ANONYMOUS_LINKS_EXPIRE_IN_DAYS.RECOMMENDED', {
        count: getAssessment('requireAnonymousLinksExpireInDays').recommended,
      }),
      notApplicableMessageId: ANONYMOUS_LINK_EXPIRY_NOT_APPLICABLE_MESSAGE_ID,
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.SHAREPOINT.REQUIRE_ANONYMOUS_LINKS_EXPIRE_IN_DAYS',
    },
    {
      key: 'markNewFilesSensitiveByDefault',
      sourceKey: 'markNewFilesSensitiveByDefault',
      labelId: 'AI_RISK_REPORT.SETTINGS.SHAREPOINT.NEW_FILES_SENSITIVE',
      ...getAssessment('markNewFilesSensitiveByDefault'),
      current: formatMarkNewFilesSensitiveByDefaultValue(getAssessment('markNewFilesSensitiveByDefault').current),
      recommended: formatRecommendedBooleanValue(getAssessment('markNewFilesSensitiveByDefault').recommended),
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.SHAREPOINT.MARK_NEW_FILES_SENSITIVE_BY_DEFAULT',
    },
    {
      key: 'enableRestrictedAccessControl',
      sourceKey: 'enableRestrictedAccessControl',
      labelId: 'AI_RISK_REPORT.SETTINGS.SHAREPOINT.RESTRICTED_ACCESS_CONTROL_ENABLED',
      ...getAssessment('enableRestrictedAccessControl'),
      current: getAssessment('enableRestrictedAccessControl').licenseRequired
        ? formatMessage(intl, 'AI_RISK_REPORT.SHAREPOINT.RESTRICTED_ACCESS_CONTROL.LICENSE_REQUIRED')
        : formatBooleanValue(getAssessment('enableRestrictedAccessControl').current),
      recommended: formatRecommendedBooleanValue(getAssessment('enableRestrictedAccessControl').recommended),
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.SHAREPOINT.ENABLE_RESTRICTED_ACCESS_CONTROL',
    },
  ]
}
